import { requireContentRulesAccepted } from "./contentRules";
import { db, auth } from "./firebase";
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
  increment,
} from "firebase/firestore";
import logger from "./logger";
import { publicBaseUrl } from "./baseUrl";

// ── SHA-256 hashing ──
async function sha256(text) {
  const encoder = new TextEncoder();
  const data = encoder.encode(text.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ── Generate short puzzle ID (12 chars, URL-safe) ──
// Exported so a creator can reserve the id up front: media uploaded mid-flow is
// stored under puzzles/{id}/media and must end up belonging to that same puzzle.
export function generatePuzzleId() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  const values = crypto.getRandomValues(new Uint8Array(12));
  for (let i = 0; i < 12; i++) {
    id += chars[values[i] % chars.length];
  }
  return id;
}

const PUZZLE_ID_RE = /^[A-Za-z0-9]{12}$/;

// ── Reveal media (Phase 8) ──
// Keep only the shape the Rules accept: { type: "photo" | "song" | "video", url }. The
// Rules also pin the URL to the Storage host; this just drops anything that is
// obviously not media so a bad value can't cost the creator a failed publish.
function normalizeFinale(finale) {
  if (!finale || typeof finale !== "object") return null;
  const { type, url } = finale;
  if (type !== "photo" && type !== "song" && type !== "video") return null;
  if (typeof url !== "string" || url.length === 0 || url.length > 2048) return null;
  return { type, url };
}

// ── Rate limiting (client-side, max 10 puzzles per hour) ──
const RATE_LIMIT_KEY = "puzzly_rate";
const RATE_LIMIT_MAX = 10;
const RATE_LIMIT_WINDOW = 60 * 60 * 1000; // 1 hour

function checkRateLimit() {
  try {
    const raw = sessionStorage.getItem(RATE_LIMIT_KEY);
    const entries = raw ? JSON.parse(raw) : [];
    const now = Date.now();
    const recent = entries.filter((t) => now - t < RATE_LIMIT_WINDOW);
    if (recent.length >= RATE_LIMIT_MAX) {
      return false;
    }
    recent.push(now);
    sessionStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recent));
    return true;
  } catch {
    return true; // fail open if sessionStorage isn't available
  }
}

// ── Create a puzzle ──
//
// Phase 7 / 07-02 refactor: replaces the old weekly-cap batch write with a
// direct activePuzzleCount: increment(1) on users/{uid}. The
// puzzle doc write + count increment commit atomically via writeBatch.
//
// `opts.profile` is the latest users/{uid} snapshot passed from App.jsx
// (subscribed via onSnapshot). Used only for type-checking/logging; the
// slot predicate in Rules is the authoritative gate.
//
// Error handling: `permission-denied` from the batch.commit() typically
// means the slot predicate (T-07-04) caught an over-cap attempt that the
// client-side `canCreatePuzzle(profile)` check missed (stale snapshot,
// two-tab race, or a malicious bypass). We surface this as CAP_REACHED so
// the caller can mount CapBlockerModal instead of the generic error banner.
export async function createPuzzle(config, opts = {}) {
  // Prima di qualsiasi contenuto, le regole accettate (#65).
  await requireContentRulesAccepted({ profile: opts.profile });
  if (!checkRateLimit()) {
    throw new Error("You've created too many puzzles recently. Please wait a bit before creating another one.");
  }

  // A creator that already uploaded media reserved its id (config._puzzleId);
  // anything that isn't a well-formed id is ignored and a fresh one is drawn.
  const puzzleId = PUZZLE_ID_RE.test(config._puzzleId || "") ? config._puzzleId : generatePuzzleId();
  // Every puzzle is stamped with the creator's uid so the ownerUid rules
  // guard can enforce write authorship. If auth has not bootstrapped yet we
  // send `null` — the rules tolerate this for legacy backward-compat.
  const ownerUid = auth.currentUser?.uid || null;
  logger.info("Creating puzzle", { puzzleId, theme: config.theme, title: config.title, levels: config.levels.length, ownerUid });

  // Hash the secret word — unless there is none. An unsealed puzzle (the word is
  // optional from Phase 8) stores "" unhashed; see isSealed().
  const hashedPassword = isSealed(config) ? await sha256(config.password) : "";

  // Process levels: hash answers, keep celebration content as-is
  const processedLevels = await Promise.all(
    config.levels.map(async (level) => {
      const hashedAnswer = await sha256(level.answer);

      return {
        id: level.id,
        title: level.title,
        question: level.question,
        answer: hashedAnswer,
        hint: level.hint || "",
        celebrationType: level.celebrationType || "default",
        celebrationContent: level.celebrationContent || "",
      };
    })
  );

  const puzzleDoc = {
    version: 1,
    theme: config.theme,
    title: config.title,
    password: hashedPassword,
    passwordHint: config.passwordHint || "",
    finalMessage: config.finalMessage || "",
    createdAt: serverTimestamp(),
    ownerUid, // null tolerated by rules for legacy/forward compat
    levels: processedLevels,
  };
  // Phase 8 — optional photo or song on the reveal. Only written when present,
  // so a puzzle without it keeps the same shape (and field count) as before.
  const finale = normalizeFinale(config.finale);
  if (finale) puzzleDoc.finale = finale;

  // Phase 7 atomic batch: puzzle doc + activePuzzleCount increment.
  // Falls back to a plain setDoc when there's no ownerUid (legacy path —
  // slot predicate doesn't apply).
  try {
    if (ownerUid) {
      const batch = writeBatch(db);
      batch.set(doc(db, "puzzles", puzzleId), puzzleDoc);
      // activePuzzleCount increment replaces weeklyCount/recentPuzzles writes.
      batch.update(doc(db, "users", ownerUid), {
        activePuzzleCount: increment(1),
      });
      await Promise.race([
        batch.commit(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Firestore write timed out. Please ensure Firestore Database is enabled in your Firebase console (Build → Firestore Database → Create Database → Start in test mode).")), 15000)
        ),
      ]);
    } else {
      // Legacy path — no ownerUid, no slot counter update, no batch.
      await Promise.race([
        setDoc(doc(db, "puzzles", puzzleId), puzzleDoc),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Firestore write timed out. Please ensure Firestore Database is enabled in your Firebase console (Build → Firestore Database → Create Database → Start in test mode).")), 15000)
        ),
      ]);
    }
    logger.info("Puzzle created successfully", { puzzleId });
  } catch (err) {
    // Distinguish slot-deny from other failures so the caller can mount
    // CapBlockerModal (vs generic error). Firestore SDK maps Rules `false`
    // returns to code === "permission-denied".
    if (err && err.code === "permission-denied") {
      logger.warn("createPuzzle denied by Rules — likely cap-hit (CAP_REACHED)", {
        puzzleId,
        ownerUid,
      });
      const capErr = new Error("CAP_REACHED");
      capErr.code = "permission-denied";
      capErr.original = err;
      throw capErr;
    }
    logger.error("Failed to create puzzle", { puzzleId, error: err.message });
    throw err;
  }

  const shareUrl = `${publicBaseUrl()}#${puzzleId}`;

  return { id: puzzleId, shareUrl, doc: puzzleDoc };
}

// ── Hard-delete a puzzle ──
//
// Phase 7 / 07-02 — removes the canonical doc, the user history mirror, and
// decrements activePuzzleCount atomically via writeBatch. Rules require the
// caller's uid to match resource.data.ownerUid; a non-owner delete will be
// rejected server-side.
//
// Mirror write (users/{uid}/puzzles/{puzzleId}) is deleted in the same batch
// so slot accuracy and history consistency are both atomic.
/**
 * Hard-delete a puzzle: removes canonical doc + user mirror + decrements slot count.
 * All three writes are atomic via writeBatch. Rules require ownerUid match.
 *
 * @param {string} puzzleId
 * @param {string} uid — must match puzzle's ownerUid (Rules enforce this)
 * @returns {Promise<void>}
 */
export async function deletePuzzle(puzzleId, uid) {
  if (!puzzleId || !uid) throw new Error("deletePuzzle requires puzzleId and uid");
  logger.info("deletePuzzle", { puzzleId, uid });
  const batch = writeBatch(db);
  batch.delete(doc(db, "puzzles", puzzleId));
  batch.delete(doc(db, "users", uid, "puzzles", puzzleId));
  batch.update(doc(db, "users", uid), {
    activePuzzleCount: increment(-1),
  });
  try {
    await Promise.race([
      batch.commit(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("deletePuzzle timed out")), 15000)
      ),
    ]);
    logger.info("deletePuzzle success", { puzzleId, uid });
  } catch (err) {
    logger.error("deletePuzzle failed", { puzzleId, uid, error: err.message, code: err.code });
    throw err;
  }
}

// ── Fetch a puzzle by ID ──
export async function fetchPuzzle(puzzleId) {
  logger.info("Fetching puzzle", { puzzleId });
  try {
    const docRef = doc(db, "puzzles", puzzleId);
    const docSnap = await Promise.race([
      getDoc(docRef),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Firestore read timed out")), 10000)
      ),
    ]);

    if (!docSnap.exists()) {
      logger.warn("Puzzle not found", { puzzleId });
      return null;
    }

    const data = docSnap.data();
    logger.info("Puzzle fetched successfully", { puzzleId, theme: data.theme, levels: data.levels?.length });
    return {
      ...data,
      _id: puzzleId,
    };
  } catch (err) {
    logger.error("Failed to fetch puzzle", { puzzleId, error: err.message });
    throw err;
  }
}

// ── Sealed or not ──
// Phase 8 makes the secret word optional. An unsealed puzzle stores `password: ""`.
// That value is unambiguous: a sealed Firestore puzzle stores a 64-char SHA-256
// digest, and a legacy LZ link carries the plain word, which the old creator
// required to be at least 3 characters. Only an explicit empty (or whitespace-
// only) string means "no gate"; a missing field keeps the old behaviour and is
// treated as sealed, so no existing puzzle changes how it opens.
export function isSealed(puzzle) {
  const pw = puzzle ? puzzle.password : undefined;
  if (typeof pw !== "string") return true;
  return pw.trim() !== "";
}

// ── Check password against hash ──
export async function checkPassword(input, hashedPassword) {
  const hashed = await sha256(input);
  return hashed === hashedPassword;
}

// ── Check answer against hash ──
export async function checkAnswer(input, hashedAnswer) {
  const hashed = await sha256(input);
  return hashed === hashedAnswer;
}

// ── Player progress (Firestore) ──
export async function updatePlayerProgress(puzzleId, currentLevel, completed) {
  try {
    const docRef = doc(db, "puzzles", puzzleId);
    await updateDoc(docRef, {
      playerProgress: {
        currentLevel,
        completed,
        updatedAt: serverTimestamp(),
      },
    });
    logger.debug("Player progress updated", { puzzleId, currentLevel, completed });
  } catch (err) {
    logger.error("Failed to update player progress", { puzzleId, error: err.message });
  }
}

export async function getPlayerProgress(puzzleId) {
  try {
    const docRef = doc(db, "puzzles", puzzleId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const data = docSnap.data();
    return data.playerProgress || null;
  } catch {
    return null;
  }
}

// ── Extract puzzle ID from a share URL ──
export function extractPuzzleId(url) {
  try {
    if (url.includes("#")) {
      const hash = url.split("#")[1];
      if (hash && hash.length <= 30) return hash;
    }
    return null;
  } catch {
    return null;
  }
}

// ── Drafts (design 2026-06-30) ──
// One unpublished puzzle per user at drafts/{uid}. Does NOT consume a slot and
// has no share URL — publishing later runs createPuzzle() (the slot gate) and
// then deleteDraft(). config holds PLAINTEXT answers/password; safe only under
// the owner-only read rule on drafts/{uid}.

export async function saveDraft(config, creatorType) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("AUTH_REQUIRED");
  const payload = {
    ownerUid: uid,
    creatorType,
    title: config.title || "",
    theme: config.theme,
    levelCount: Array.isArray(config.levels) ? config.levels.length : 0,
    config,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  logger.info("Saving draft", { uid, creatorType, levelCount: payload.levelCount });
  await Promise.race([
    setDoc(doc(db, "drafts", uid), payload),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Saving draft timed out. Check your connection and try again.")), 15000)
    ),
  ]);
}

export async function fetchDraft() {
  const uid = auth.currentUser?.uid;
  if (!uid) return null;
  const snap = await getDoc(doc(db, "drafts", uid));
  return snap.exists() ? snap.data() : null;
}

export async function deleteDraft() {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await deleteDoc(doc(db, "drafts", uid));
}
